<template>
  <div class="gui">
    <div class="gui__score">
      <div class="gui-icon gui-icon--score"></div>
      <span>×</span>
      <span class="score-value">{{ coinCount }}</span>
    </div>

    <div class="gui__life">
      <div class="gui-life">
        <div
          v-for="i in Math.max(0, Math.min(3, lifeCount))"
          :key="i"
          class="gui-icon gui-icon--life"
        ></div>
      </div>
    </div>

    <div class="gui__sound">
      <div
        class="gui-button"
        :class="{ muted: isMuted }"
        @click="$emit('toggle-mute')"
        role="button"
        aria-label="Toggle sound"
        :aria-pressed="isMuted"
      >
        <div class="gui-icon gui-icon--sound"></div>
      </div>
    </div>

    <div class="gui__pause">
      <div
        class="gui-button"
        :class="{ paused: isPaused }"
        @click="$emit('toggle-pause')"
        role="button"
        aria-label="Toggle pause"
        :aria-pressed="isPaused"
      >
        <div class="gui-icon gui-icon--pause"></div>
      </div>
    </div>

    <div class="gui__overlay"></div>

    <div class="gui__triggers">
      <div v-if="currentButton === 'start'">
        <button
          class="gui-button gui-button--start"
          @click="$emit('start')"
          key="start"
        >
          <div class="gui-icon gui-icon--start"></div>
        </button>
      </div>

      <div v-else-if="currentButton === 'restart'">
        <div class="gui-final-score">
          Score: {{ coinCount }}
        </div>

        <button
          class="gui-button gui-button--restart"
          @click="$emit('restart')"
          key="restart"
        >
          <div class="gui-icon gui-icon--restart"></div>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
defineProps<{
  coinCount: number;
  lifeCount: number;
  isMuted: boolean;
  isPaused: boolean;
  currentButton: 'start' | 'restart' | null;
}>();

defineEmits<{
  (e: 'toggle-mute'): void;
  (e: 'toggle-pause'): void;
  (e: 'start'): void;
  (e: 'restart'): void;
}>();
</script>

<style scoped lang="scss">
.gui {
  display: grid;
  grid-template: auto auto 1fr / 1fr auto auto;
  gap: 5px 0;
  position: absolute;
  inset: 0;
  z-index: 1000;
  margin: auto;
  pointer-events: none;

  &__score {
    grid-row: 1 / 2;
    grid-column: 1 / 2;
    display: flex;
    align-items: center;
    align-self: center;
    gap: 0 4px;
    padding-left: 10px;
    padding-top: 10px;
    color: #fff;
    font-family: "Jersey 10", sans-serif;
    font-weight: 400;
    font-style: normal;
    font-size: 29px;
  }

  &__life {
    grid-row: 2 / 3;
    grid-column: 1 / 2;
    align-self: center;
    padding-left: 10px;

    .gui-life {
      display: flex;
      align-items: center;
      height: 32px;
    }
  }

  &__sound {
    grid-row: 1 / 3;
    grid-column: 2 / 3;
    align-self: center;
    padding-top: 10px;

    .gui-button {
      width: 64px;
    }
  }

  &__pause {
    grid-row: 1 / 3;
    grid-column: 3 / 4;
    align-self: center;
    padding-right: 20px;
    padding-top: 10px;

    .gui-button {
      width: 64px;
    }

    .game--paused & {
      z-index: 3;
    }
  }

  &__overlay {
    grid-row: 1 / 4;
    grid-column: 1 / 4;
    background-color: rgba(3, 9, 11, 1);
    z-index: 1;
    display: grid;
    place-content: center;
    gap: 0;

    .game--ready & {
      opacity: 1;
    }

    .game--running & {
      opacity: 0;
      transition: opacity 2000ms 250ms cubic-bezier(0.22, 1, 0.36, 1);
    }

    .game--dying & {
      opacity: 1;
      transition: opacity 3000ms 500ms ease;
    }

    .game--paused & {
      opacity: 0.85;
      transition: opacity 750ms ease;
    }
  }

  &__triggers {
    grid-row: 1 / 4;
    grid-column: 1 / 4;
    z-index: 1;
    display: grid;
    place-content: center;
    gap: 0;

    .game--ready & {
      opacity: 1;
    }

    .game--running & {
      opacity: 0;
      transition: opacity 1000ms 250ms ease;
    }

    .game--dying & {
      opacity: 1;
      transition: opacity 1000ms 1500ms ease;
    }

    .game--paused & {
      opacity: 0.75;
      transition: opacity 1000ms ease;
    }
  }
}

.gui-button {
  height: 64px;
  display: grid;
  place-content: center;
  gap: 0;
  pointer-events: auto;
  cursor: pointer;
}

.gui-icon {
  background-size: 220px 256px;
  background-repeat: no-repeat;

  &--score,
  &--life,
  &--sound,
  &--pause,
  &--start,
  &--restart {
    background-image: url("@/assets/images/webp/gui-icons.webp");
  }

  &--score {
    width: 32px;
    height: 32px;
    background-position: 0 0;
  }

  &--life {
    width: 32px;
    height: 32px;
    background-position: 0 -32px;
  }

  &--sound {
    width: 32px;
    height: 32px;
    background-position: 0 -64px;

    .muted & {
      background-position: -32px -64px;
    }
  }

  &--pause {
    width: 32px;
    height: 32px;
    background-position: 0 -96px;

    .paused & {
      background-position: -32px -96px;
    }
  }

  &--start {
    width: 220px;
    height: 64px;
    background-position: 0 -128px;
  }

  &--restart {
    width: 220px;
    height: 64px;
    background-position: 0 -192px;
  }
}

.gui-final-score {
  padding: 12px;
  text-align: center;
  font-family: "Jersey 10", sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 34px;
  color: #fff;
}
</style>
